import React, { useCallback, useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import LoadingIndicator from "react-loading-indicator";

import * as SC from "./CampaignCreateMediaLibrary.styled";

import InfiniteScroll from "react-infinite-scroll-component";
import { withLDConsumer } from "launchdarkly-react-client-sdk";
import {
  Store__System,
  CustomerSucessController,
  Button as BtnPrimary
} from "@talentbait/shared-components";

import logger from "../../../../utils/LoggerFrontend";
import Actions from "../../../../Actions";
import { withRefluxStore, MediaLibraryStore, PreviewStore } from "../../../../Stores";

import { SidePanel, SearchBar } from "../Components";
import { TabSlider, TabContent } from "../Components/TabSlider";
import NoResults from "../NoResults";
import SearchClaims from "../Components/SearchClaims";
import Terms from "../Components/Terms";
import UploadPhotoButton from "../../../../Components/UploadPhotoBtn";
import Loader from "../Loader";

import { isVideo, noop } from "../utils";
import { SHUTTERSTOCK_API_VERSION } from "../../../../constants";

import {
  stockUrlToStockId,
  cleanTitle,
  cleanTitleForKeywordsSearch
} from "../../../../utils/Helpers";
import { getCompanyImageFolderName } from "../../../../utils/GetCompanyFolderName";
import CloudIcon from "../../../../../../public/images/upload_cloud.svg";
import useSearchByKeywords from "../../../../hooks/useSearchByKeywords";
import { createShutterstockQuery } from "../helpers";
import useArchivePictureModal from "../../../../hooks/useArchivePictureModal";
import CreativeOption from "./creative-option/CreativeOption";

const SHUTTERSTOCK = "shutterstock";

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const CampaignCreateMediaLibrary = ({
  isMediaLibraryOpen,
  cancelSelected,
  saveSelected,
  selectedCreative,
  updateSelectedCreative,
  updateSelectedAsset,
  archivePicture,
  isArchivable,
  disableStock,
  allowVideos,
  allowImages = true,
  isLogo,
  // Store
  recent,
  recentLogos,
  stock,
  uploaded,
  website,
  stockSearch,
  stockKeywordsSearch,
  keywordsPresent,
  stockPage,
  stockTotal,
  loadingRecent,
  loadingStock,
  loadingStockPurchase,
  loadingWebsite,
  licensedImage,
  campaignData,
  jobCategoryData,
  canBuyStockPicture,
  canBeEmpty,
  userProfile,
  organization,
  flags,
  keywordsLoading,
  requestId,
  keywords
}) => {
  // Defines use transaltion object
  const { t } = useTranslation();

  const [selectedTab, setSelectedTab] = useState(t("Prev Used"));
  const [filteredRecent, setFilteredRecent] = useState(isLogo ? recentLogos : recent);
  const [showTerms, setShowTerms] = useState(false);
  const [isFirstSearch, setIsFirstSearch] = useState(true);
  const [isKeywordsPresent, setIsKeywordsPresent] = useState(false);
  const [jobCategorySearchQuery, setJobCategorySearchQuery] = useState(
    jobCategoryData?.searchQuery || undefined
  );

  const { jobTitle, companyName, id: campaignId } = campaignData || {};
  const organizationId = campaignData?.organizationId || organization?.id;
  const orgaName = campaignData?.orgaName || organization?.name;
  const companyId = campaignData?.companyId;

  // Update opened once state if it was opened
  useEffect(() => {
    const unsubscribeJobCategorySearchQuery = Actions.getJobCategoryWithId.completed.listen(
      (jobCategory) => {
        logger.debug(
          "SelfServiceCampaignCreation:MediaLibrary",
          "Received job category",
          jobCategory
        );
        setJobCategorySearchQuery(jobCategory?.searchQuery || null);
      }
    );
    return () => {
      unsubscribeJobCategorySearchQuery();
    };
  }, [isMediaLibraryOpen, campaignData, jobCategoryData, jobCategorySearchQuery]);

  // Update selected image when a shutterstock picture is licensed
  useEffect(() => {
    if (licensedImage) {
      logger.log("MediaLibrary", "finished licensing image", { licensedImage });
      updateSelectedCreative(licensedImage);
    }
  }, [licensedImage, updateSelectedCreative]);

  useEffect(() => {
    if (isMediaLibraryOpen) {
      setShowTerms(false);
    }
  }, [isMediaLibraryOpen]);

  const defaultStockSearchText = useMemo(() => {
    if (isKeywordsPresent) {
      return stockKeywordsSearch;
    }
    return cleanTitle(jobTitle) || "Business";
  }, [jobTitle, keywords]);

  // Init shutterstock search if we are on the stock search tab
  const shouldSearchShutterStock = useMemo(() => selectedTab.includes("Stock"), [selectedTab]);
  const shutterStockAPIVersion = !organization?.isAgency ? SHUTTERSTOCK_API_VERSION : undefined;

  // Save uploaded image to media library, select it by default
  const handlePictureUploaded = useCallback(
    async ({ url }) => {
      Actions.saveUploadedPicture(url);
      updateSelectedCreative(url);
      // Wait 3 seconds for the images to be processed in Cloudinary
      await sleep(3000);
      Actions.getHistoricImages({ companyName, orgaId: organizationId, orgaName });
    },
    [updateSelectedCreative, orgaName, companyName, organizationId]
  );

  // Search into Shutterstock API with a keywords query
  useSearchByKeywords({
    defaultSearch: defaultStockSearchText,
    fallbackSearch: jobCategorySearchQuery,
    requestId,
    keywords,
    shutterStockAPIVersion,
    setIsKeywordsPresent
  });

  const searchShutterStock = useCallback(
    (search) => {
      if (isFirstSearch) {
        return;
      }

      // Segment tracking
      CustomerSucessController.send("MediaLibrary Tab Selected", {
        tab: "Stock Images",
        jobTitle,
        campaignId,
        userName: userProfile.userName,
        companyName: orgaName,
        orgaId: organizationId
      });

      logger.log(
        "CampaignCreateMediaLibrary:searchShutterstockImagesAPI",
        "search and stockSearch",
        { search, stockSearch, stockKeywordsSearch }
      );

      // If the searchtext didn't change then no need to search for the shutterstock again
      if (search === stockSearch || search === stockKeywordsSearch) {
        return;
      }

      setIsKeywordsPresent(false);

      // Segment tracking
      CustomerSucessController.send("Stock Image Searched", {
        search,
        campaignId,
        orgaId: organizationId,
        companyName: orgaName,
        jobTitle,
        userName: userProfile.userName
      });

      const ref = Actions.searchShutterstockImagesAPI.completed.listen((result) => {
        const { payload, query } = result;
        logger.log(
          "CampaignCreateMediaLibrary:searchShutterstockImagesAPI",
          "images search returned",
          {
            images: payload.data?.length || 0,
            fallback: payload.fallback,
            search: query.search,
            fallbackSearch: query.fallbackSearch,
            user: { userId: query.userProfile?.userId, userName: query.userProfile?.userName },
            companyName,
            orgaName
          }
        );
        ref();
      });

      const refFailed = Actions.searchShutterstockImagesAPI.failed.listen((error) => {
        logger.log(
          "error:CampaignCreateMediaLibrary:searchShutterstockImagesAPI",
          "images search failed",
          {
            error: error?.meta?.message || error.message,
            fallback: error.fallback,
            search: error.search,
            fallbackSearch: error.fallbackSearch,
            userProfile: error.userProfile,
            companyName,
            orgaName
          }
        );
        refFailed();
      });

      if (search) {
        Actions.searchShutterstockImagesAPI({
          defaultSearch: defaultStockSearchText,
          search,
          fallbackSearch: jobCategorySearchQuery,
          userProfile,
          version: shutterStockAPIVersion,
          keywords: false
        });
      }
    },
    [
      companyName,
      campaignId,
      jobTitle,
      orgaName,
      organizationId,
      jobCategorySearchQuery,
      shutterStockAPIVersion,
      stockSearch,
      stockKeywordsSearch,
      keywordsPresent,
      userProfile,
      defaultStockSearchText
    ]
  );

  // Append shutterstock images with new ones
  const loadMoreShutterStockImages = useCallback(() => {
    logger.log("loadMoreShutterStockImages", "loading more shutterstock images", {
      isKeywordsPresent,
      stockKeywordsSearch,
      stockSearch,
      defaultStockSearchText
    });

    let keywords = false;
    let typeOfSearch = "";
    if (isKeywordsPresent) {
      typeOfSearch = stockKeywordsSearch;
      keywords = true;
    } else {
      typeOfSearch = stockSearch;
    }

    Actions.searchShutterstockImagesAPI({
      defaultSearch: defaultStockSearchText,
      search: typeOfSearch,
      fallbackSearch: jobCategorySearchQuery,
      page: stockPage + 1,
      userProfile,
      keywords,
      version: shutterStockAPIVersion
    });
  }, [
    isFirstSearch,
    jobCategorySearchQuery,
    shutterStockAPIVersion,
    stockPage,
    stockSearch,
    stockKeywordsSearch,
    isKeywordsPresent,
    userProfile
  ]);

  // Buy shutterstock picture
  const buySelectedShutterstockImage = useCallback((imageUrl) => {
    logger.log("MediaLibrary:buySelectedShutterstockImage:useCallback", "called", { imageUrl });
    const imageId = stockUrlToStockId({ url: imageUrl });
    Actions.licenseShutterstockImageAPI({ imageIds: [imageId] });
  }, []);

  // Upload shutterstock picture
  logger.log("MediaLibrary:buySelectedShutterstockImage", "flag shutterstock", {
    flags
  });

  // Uploaded images without the selected one (it is listed on the same tab)
  const uploadedWithoutSelected = useMemo(
    () => uploaded.filter((creativeUrl) => creativeUrl !== selectedCreative),
    [selectedCreative, uploaded]
  );

  const displayStockTab = !disableStock && campaignData?.source !== "ONLYFY";

  const tabs =
    displayStockTab && allowImages
      ? [t("Prev Used"), t("Stock Images"), t("From Website"), t("Selected")]
      : [t("Prev Used"), t("From Website"), t("Selected")];

  // Memoize filteredRecent to avoid unnecessary recomputations
  const filteredRecentUpdate = useMemo(() => {
    let updatedFilteredRecent = isLogo ? recentLogos : recent;

    if (!allowImages && !allowVideos) {
      // If both allowImages and allowVideos are false, we should clear the array
      return [];
    }

    if (!allowImages) {
      updatedFilteredRecent = (updatedFilteredRecent || []).filter(({ image_url }) =>
        isVideo(image_url)
      );
    }

    if (!allowVideos) {
      updatedFilteredRecent = (updatedFilteredRecent || []).filter(
        ({ image_url }) => !isVideo(image_url)
      );
    }

    return updatedFilteredRecent;
  }, [recent, recentLogos, isLogo, allowImages, allowVideos]);

  useEffect(() => {
    const isRecentEmpty = !recent || recent.length === 0;
    const isRecentLogosEmpty = !recentLogos || recentLogos.length === 0;

    const avoidFiltering = loadingRecent || (isRecentEmpty && isRecentLogosEmpty);

    if (avoidFiltering) return;

    setFilteredRecent(filteredRecentUpdate);
  }, [filteredRecentUpdate, loadingRecent, recent, recentLogos]);

  const { archivePictureModalCallback } = useArchivePictureModal(
    campaignData,
    userProfile,
    archivePicture,
    isArchivable,
    setFilteredRecent
  );

  const { folderName } = useMemo(
    () =>
      getCompanyImageFolderName({
        orgaId: organizationId,
        companyId,
        orgaName,
        companyName
      }),
    [organizationId, companyId, orgaName, companyName]
  );

  // Cloudinary metadata
  const cloudinaryContext = useMemo(() => {
    let context = `orgaId=${organizationId}|source=campaign-create-media-library`;

    if (campaignId) {
      context += `|campaignId=${campaignId}`;
    }

    return context;
  }, [organizationId, campaignId]);

  const handleGetKeywordsByJobTitle = useCallback(() => {
    const refCompleted = Actions.getKeywordsByJobTitle.completed.listen((keywords) => {
      logger.log("MediaLibrary:getKeywordsByJobTitle:completed", "keywords received", {
        keywords
      });
      if (keywords.length === 0) {
        logger.log(
          "MediaLibrary:getKeywordsByJobTitle:completed",
          "requesting to create keywords",
          {
            keywords,
            campaignId
          }
        );
        Actions.createJobTitleKeywords({ title: defaultStockSearchText });
      } else {
        const search = createShutterstockQuery(keywords);

        logger.log(
          "MediaLibrary:getKeywordsByJobTitle:completed",
          "requesting shutterstock images from keywords",
          {
            keywords,
            defaultStockSearchText,
            campaignId
          }
        );
        setIsKeywordsPresent(true);

        Actions.searchShutterstockImagesAPI({
          defaultSearch: defaultStockSearchText,
          fallbackSearch: jobCategorySearchQuery,
          keywords: true,
          search,
          version: shutterStockAPIVersion,
          page: stockPage
        });
      }
      refCompleted();
    });

    Actions.getKeywordsByJobTitle({ search: cleanTitleForKeywordsSearch(defaultStockSearchText) });
    setIsFirstSearch(false);
  }, []);

  function handleTabClick(tab) {
    if (tab === t("Stock Images")) {
      logger.log(
        "MediaLibrary:getKeywordsByJobTitle:",
        `requesting keywords with jobtitle: ${defaultStockSearchText}`,
        {}
      );
    }
    handleGetKeywordsByJobTitle();
    setSelectedTab(tab);
  }

  return (
    <>
      {!organization?.isAgency ? (
        <SidePanel
          active={showTerms && isMediaLibraryOpen}
          side="left"
          offset={10}
          padding="16px"
          height="auto"
          id="terms-campaign-create-media-library"
        >
          <Terms />
        </SidePanel>
      ) : null}

      <SidePanel
        active={isMediaLibraryOpen}
        side="right"
        offset={10}
        id="campaign-create-media-library-panel"
      >
        <TabSlider
          tabs={tabs}
          selectedTab={selectedTab}
          setSelectedTab={handleTabClick}
          width={384}
          footer={
            <SC.EditorUpdateActions>
              <BtnPrimary mr={10} type="tertiary" variant="outline" onClick={cancelSelected}>
                {t("Cancel")}
              </BtnPrimary>
              <BtnPrimary
                type="secondary"
                onClick={saveSelected}
                disabled={!selectedCreative && !canBeEmpty}
              >
                {t("Select")}
              </BtnPrimary>
            </SC.EditorUpdateActions>
          }
        >
          <TabContent>
            {loadingRecent ? (
              <SC.LoadingIndicatorWrapper>
                <LoadingIndicator />
              </SC.LoadingIndicatorWrapper>
            ) : filteredRecent.length ? (
              <SC.Content>
                <SC.CreativesWrapper creativesCount={filteredRecent.length}>
                  {filteredRecent.map((creative) => (
                    <CreativeOption
                      key={creative.image_url}
                      url={creative.image_url}
                      root={creative.source}
                      externalId={creative.externalId}
                      active={selectedCreative === creative.image_url}
                      updateSelectedCreative={updateSelectedCreative}
                      updateSelectedAsset={updateSelectedAsset}
                      archivePicture={archivePictureModalCallback}
                    />
                  ))}
                </SC.CreativesWrapper>
              </SC.Content>
            ) : (
              <SC.CreativesText>{t("No Results Found")}</SC.CreativesText>
            )}
          </TabContent>

          {displayStockTab && allowImages && (
            <TabContent id="scrollableStockDiv">
              <SearchBar
                defaultText={defaultStockSearchText}
                debounce={1000}
                onChange={shouldSearchShutterStock ? searchShutterStock : noop}
              />

              <SearchClaims onClick={() => setShowTerms(!showTerms)} />

              <SC.StockContent>
                {(keywordsLoading || loadingStock) && !stock.length ? (
                  <SC.LoaderWrapper>
                    <Loader size={75} />
                    <img src="/images/bilderlotse-logo.png" />
                    <p>
                      {t(
                        "Posts that include images generate 650% higher engagement than text-only posts"
                      )}
                    </p>
                  </SC.LoaderWrapper>
                ) : stock.length ? (
                  <InfiniteScroll
                    style={{ overflow: "visible" }}
                    scrollableTarget="scrollableStockDiv"
                    dataLength={stock.length}
                    next={loadMoreShutterStockImages}
                    hasMore={stockTotal > stock.length}
                    loader={
                      <SC.LoadingIndicatorWrapper>
                        <LoadingIndicator />
                      </SC.LoadingIndicatorWrapper>
                    }
                    endMessage={
                      <p style={{ textAlign: "center" }}>
                        <b>Fertig</b>
                      </p>
                    }
                  >
                    <SC.CreativesWrapper creativesCount={stock.length} flex>
                      {stock.map((creative) => (
                        <CreativeOption
                          imageId={creative.id}
                          searchId={creative.searchId}
                          source={SHUTTERSTOCK}
                          key={creative.assets.huge_thumb.url}
                          url={creative.assets.huge_thumb.url}
                          active={selectedCreative === creative.assets.huge_thumb.url}
                          updateSelectedCreative={updateSelectedCreative}
                          updateSelectedAsset={updateSelectedAsset}
                          canBuyStockPicture={canBuyStockPicture}
                          loadingStockPurchase={loadingStockPurchase}
                          buyStockPicture={buySelectedShutterstockImage}
                        />
                      ))}
                    </SC.CreativesWrapper>
                  </InfiniteScroll>
                ) : (
                  <NoResults />
                )}
              </SC.StockContent>
            </TabContent>
          )}

          <TabContent>
            {loadingWebsite ? (
              <SC.LoadingIndicatorWrapper>
                <LoadingIndicator />
              </SC.LoadingIndicatorWrapper>
            ) : website.length ? (
              <SC.CreativesWrapper creativesCount={website.length}>
                {website.map((creative) => (
                  <CreativeOption
                    key={creative.image_url}
                    url={creative.image_url}
                    active={selectedCreative === creative.image_url}
                    updateSelectedCreative={updateSelectedCreative}
                    updateSelectedAsset={updateSelectedAsset}
                  />
                ))}
              </SC.CreativesWrapper>
            ) : (
              <SC.CreativesText>{t("No Results Found")}</SC.CreativesText>
            )}
          </TabContent>

          <TabContent>
            <SC.CreativesText>{t("Selected Images")}</SC.CreativesText>
            <SC.CreativesWrapper creativesCount={selectedCreative ? 1 : 0}>
              <CreativeOption
                url={selectedCreative}
                active
                updateSelectedCreative={updateSelectedCreative}
                updateSelectedAsset={updateSelectedAsset}
                canBuyStockPicture={canBuyStockPicture}
                loadingStockPurchase={loadingStockPurchase}
                buyStockPicture={buySelectedShutterstockImage}
                canBeEmpty={canBeEmpty}
              />
            </SC.CreativesWrapper>

            {!!uploadedWithoutSelected.length && (
              <>
                <SC.CreativesText>Uploaded</SC.CreativesText>
                <SC.CreativesWrapper creativesCount={uploadedWithoutSelected.length}>
                  {uploadedWithoutSelected.map((creativeUrl) => (
                    <CreativeOption
                      key={creativeUrl}
                      url={creativeUrl}
                      active={selectedCreative === creativeUrl}
                      updateSelectedCreative={updateSelectedCreative}
                      updateSelectedAsset={updateSelectedAsset}
                    />
                  ))}
                </SC.CreativesWrapper>
              </>
            )}

            <SC.UploadPhotoButtonWrapper>
              <UploadPhotoButton
                handlePictureChanged={handlePictureUploaded}
                showPreview={false}
                transformDimensions={false}
                showLoading
                label={"loading"}
                folderName={folderName}
                context={cloudinaryContext}
                allowVideos={allowVideos}
                allowImages={allowImages}
                isLogo={isLogo}
                buttonText={
                  <SC.UploadBoxLabel>
                    <SC.UploadBoxCloudIcon>
                      <CloudIcon stroke="#1E22FF" />
                    </SC.UploadBoxCloudIcon>
                    <div>
                      <SC.UploadText>{t("Upload Picture / Video")}</SC.UploadText>{" "}
                      {t("or drag and drop here")}
                    </div>
                  </SC.UploadBoxLabel>
                }
              />
            </SC.UploadPhotoButtonWrapper>
          </TabContent>
        </TabSlider>
      </SidePanel>
    </>
  );
};

export default withRefluxStore(
  withLDConsumer()(CampaignCreateMediaLibrary),
  [MediaLibraryStore, PreviewStore, Store__System],
  [
    "campaignData",
    "generated",
    "jobCategoryData",
    "keywordsLoading",
    "keywords",
    "licensedImage",
    "loadingRecent",
    "loadingStock",
    "loadingStockPurchase",
    "loadingWebsite",
    "organization",
    "recent",
    "recentLogos",
    "requestId",
    "stock",
    "stockPage",
    "stockSearch",
    "keywordsPresent",
    "stockKeywordsSearch",
    "stockTotal",
    "uploaded",
    "userProfile",
    "website"
  ]
);
